import { FD_BASE_URL } from "@/config";
import ShopType from "@/enum/ShopType";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { ShopOverviewResult } from "@/services/api/order";
import AuthTokenHolder from "@/services/http/AuthTokenHolder";
import { PremiumFunctionType, usePremiumValidator } from "@/services/PremiumValidator";
import { useGlobalStore } from "@/store/hooks";
import { Spin } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import React from "react";
import { useHistory } from "react-router";

interface TotalLogisticsWarningOrdersColumnProps {
    shopOverviewResult: ShopOverviewResult;
}

const TotalLogisticsWarningOrdersColumn: React.FC<TotalLogisticsWarningOrdersColumnProps> = observer(
    ({ shopOverviewResult }) => {
        const globalStore = useGlobalStore();
        const shop = globalStore.shopMapping.get(shopOverviewResult.shopId);
        const history = useHistory();
        const { validate, loading } = usePremiumValidator();

        const handleNavigate = async (orderTab: OrderTab) => {
            if (orderTab === OrderTab.LOGISTICS_WARNING) {
                const shouldNext = await validate({
                    type: PremiumFunctionType.LOGISTICS_WARNING_ORDER_LIST,
                    shopId: shopOverviewResult.shopId,
                });

                if (!shouldNext) return;
            }

            history.push(`/order/list?shopId=${shopOverviewResult.shopId}&tab=${orderTab}`);
        };

        if (!shop) return null;

        const isPddShop = [ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shop.type);
        const { totalLogisticsWarningOrders, pickupDeadlineWarningOrderCount } = shopOverviewResult;

        const renderCounter = (value: number, label = false, orderTab?: OrderTab) => {
            if (globalStore.isIframe) {
                const buildIframeUrl = (params: Record<string, any>) => {
                    const search = new URLSearchParams();
                    const token = AuthTokenHolder?.getToken?.();

                    search.set("_token_", token);

                    Object.entries(params).forEach(([key, value]) => {
                        if (value !== undefined && value !== null) {
                            search.set(key, String(value));
                        }
                    });

                    return `${FD_BASE_URL}/order/list?${search.toString()}`;
                };

                const href = buildIframeUrl({
                    shopId: shopOverviewResult.shopId,
                    tab: orderTab,
                });

                const clickable = value > 0 && orderTab;

                return (
                    <a target="_blank" href={href} style={{ textDecoration: "none", color: "inherit" }}>
                        <span
                            className={classNames({
                                label: label,
                                "label-default": value <= 0,
                                "label-danger": value > 0,
                                "cursor-pointer": clickable,
                            })}
                        >
                            {value}
                        </span>
                    </a>
                );
            }

            const clickable = value > 0 && orderTab;

            const className = classNames({
                label: label,
                "label-default": value <= 0,
                "label-danger": value > 0,
                "cursor-pointer": clickable,
            });

            return (
                <span className={className} onClick={clickable ? () => handleNavigate(orderTab) : undefined}>
                    {value}
                </span>
            );
        };

        if (isPddShop) {
            const validCounters = [
                { value: totalLogisticsWarningOrders, tab: OrderTab.LOGISTICS_WARNING },
                { value: pickupDeadlineWarningOrderCount, tab: OrderTab.PICKUP_DEADLINE_WARNING },
            ].filter((item) => item.value > 0);

            if (validCounters.length === 0) {
                return renderCounter(0, false, null);
            }

            // 动态渲染带加号的统计值
            return (
                <Spin spinning={loading}>
                    <span className="flex flex-center">
                        {validCounters.map((counter, index) => (
                            <React.Fragment key={counter.tab}>
                                {renderCounter(counter.value, true, counter.tab)}
                                {/* 在多个有效值之间显示加号 */}
                                {index < validCounters.length - 1 && <span>+</span>}
                            </React.Fragment>
                        ))}
                    </span>
                </Spin>
            );
        }

        return (
            <Spin spinning={loading}>
                {totalLogisticsWarningOrders === 0
                    ? renderCounter(0, false, null)
                    : renderCounter(totalLogisticsWarningOrders, true, OrderTab.LOGISTICS_WARNING)}
            </Spin>
        );
    },
);

export default TotalLogisticsWarningOrdersColumn;
